import type { CourseFilterInput } from '@/types/composition-functions';
import type { ListSchoolCoursesQueryVariables } from '@/types/operations';
import { useListSchoolCoursesLazyQuery } from '@/types/vue-apollo-helpers';

export const useCourses = ({
  autoFetch = false,
  first = 10,
  page = 1,
}: { autoFetch?: boolean; first?: number; page?: number }) => {
  const variables = ref<ListSchoolCoursesQueryVariables>({
    first,
    page,
    filter: {},
  });

  const {
    result: schoolCoursesResult,
    loading: loadingSchoolCourses,
    refetch: refetchSchoolCoursesQuery,
    load: loadSchoolCourses,
    error: errorSchoolCourses,
    onResult: onResultSchoolCourses,
  } = useListSchoolCoursesLazyQuery(variables);

  const refetchSchoolCourses = async(overrides?: Partial<ListSchoolCoursesQueryVariables>) => {
    await refetchSchoolCoursesQuery({ ...variables.value, ...(overrides || {}) } as ListSchoolCoursesQueryVariables);
  };
  if (autoFetch) loadSchoolCourses();

  const courses = computed(
    () => schoolCoursesResult.value?.listSchoolCourses?.data || [],
  );

  const paginatorInfo = computed(
    () => schoolCoursesResult.value?.listSchoolCourses?.paginatorInfo,
  );

  /* ---------------- FILTER ---------------- */
  const setFilter = async(filter: Partial<CourseFilterInput> & { search?: string }) => {
    variables.value.filter = { ...filter } as CourseFilterInput;
    variables.value.page = 1;
    await refetchSchoolCourses({ ...variables.value });
  };

  const clearFilter = async() => {
    variables.value.filter = {};
    variables.value.page = 1;
    await refetchSchoolCourses({ ...variables.value });
  };

  /* ---------------- PAGINATION ---------------- */
  const setPage = async(newPage: number) => {
    variables.value.page = newPage;
    await refetchSchoolCourses({ ...variables.value });
  };

  const setItemsPerPage = async(newFirst: number) => {
    variables.value.first = newFirst;
    variables.value.page = 1;
    await refetchSchoolCourses({ ...variables.value });
  };

  /* ---------------- INFINITE SCROLL / LOAD MORE ---------------- */
  const loadMore = async() => {
    variables.value.page = (variables.value.page || 1) + 1;
    await refetchSchoolCourses({ ...variables.value });
  };

  return {
    courses,
    paginatorInfo,
    loadingSchoolCourses,
    loadSchoolCourses,
    refetchSchoolCourses,
    errorSchoolCourses,
    onResultSchoolCourses,
    setPage,
    setItemsPerPage,
    setFilter,
    clearFilter,
    loadMore,
  };
};
